import { typographyTokens } from '../../theme/typographyTokens';
import * as React from 'react';

//Button Grid imports
import { Grid, Box, useTheme, Tooltip, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Paper, { PaperProps } from '@mui/material/Paper';
import { DataFieldOption } from '../../../shared/types/DataField';
import { transformQuillListToNested, nestedListStyles } from '../../utils/quillHtmlTransform';
import ArchitectureIcon from '../../assets/svg/ArchitectureIcon';
import SolutionArchitectConsultantIcon from '../../assets/svg/SolutionArchitectConsultantIcon';
import AIInviteOnlyIcon from '../../assets/svg/AIInviteOnlyIcon';
import DefaultWorkflowIcon from '../../assets/svg/DefaultWorkflowIcon';
import LightBulbIcon from '../../assets/svg/LightBulbIcon';

interface StyledPaperProps extends PaperProps {
  selected?: boolean;
  tileVariant?: 'default' | 'workflow';
}

const Item = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'tileVariant',
})<StyledPaperProps>(({ selected, tileVariant, theme }) => ({
  cursor: 'pointer',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.gray.borderGray : '#fff',
  ...theme.typography.body2,
  padding: tileVariant === 'workflow' ? theme.spacing(2) : theme.spacing(1),
  textAlign: tileVariant === 'workflow' ? 'left' : 'center',
  color: theme.palette.mode === 'dark' ? theme.palette.white.main : theme.palette.text.secondary,
  ...(tileVariant === 'workflow' ? { minHeight: 140, width: '100%' } : { height: 168, width: 168 }),
  borderRadius: '10px',
  boxShadow: '0px 1px 5px #0000001A',
  background: theme.palette.mode === 'dark' ? theme.palette.gray.borderGray + ' 0% 0% no-repeat padding-box' : '#FFFFFF 0% 0% no-repeat padding-box',
  opacity: 1,
  border: '1px solid transparent',
  '&:hover': {
    border: '1.3px solid #2372B9',
  },
  ...(selected && {
    ...theme.typography.body2,
    padding: tileVariant === 'workflow' ? theme.spacing(2) : theme.spacing(1),
    textAlign: tileVariant === 'workflow' ? 'left' : 'center',
    color: theme.palette.text.secondary,
    border: '1.3px solid #6BA543',
    background: theme.palette.mode === 'dark' ? theme.palette.gray.borderGray + ' 0% 0% no-repeat padding-box' : '#F7FBF5 0% 0% no-repeat padding-box',
    boxShadow: '10px 20px 30px #0000001A',
  }),
}));

interface TileProps {
  field: DataFieldOption;
  length: number;
  onClick: (e: any) => void;
  value?: number | number[] | string | undefined;
  selected: boolean;
  variant?: 'default' | 'workflow';
  onContinue?: () => void;
}

const CustomButton = ({
  length,
  field,
  onClick,
  value,
  selected,
  variant = 'default',
  onContinue,
}: TileProps) => {
  const { icon, value: title, description, multiselectTooltipValue } = field;
  const theme = useTheme();
  const saveOptions = () => {
    switch (length) {
      case 1:
        return 12;
      case 2:
      case 3:
        return 3.3;
      case 4:
        return 3;
      default:
        return 2.5;
    }
  };

  // Workflow cards use a branded layout distinct from regular data-field tiles.
  if (variant === 'workflow') {
    const workflowDetails = {
      'SEP Design Review': { icon: ArchitectureIcon, badge: 'Most Selected Workflow' },
      'Solution Architect Consultation': { icon: SolutionArchitectConsultantIcon, badge: 'Guidance, not Approval', isGuidance: true },
      'AI Invite Only (Restricted Use workflow)': { icon: AIInviteOnlyIcon, badge: 'Restricted Workflow', isRestricted: true },
      Ideation: { icon: LightBulbIcon, badge: 'Nuclear Only', isIdeation: true },
    }[title || ''] || { icon: DefaultWorkflowIcon, badge: '' };
    const WorkflowIcon = workflowDetails.icon;
    const { badge, isGuidance, isRestricted, isIdeation } = workflowDetails;

    return (
      <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
        <Item
          onClick={onClick}
          selected={selected}
          tileVariant={variant}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 0, md: 508 },
            padding: 0,
            overflow: 'hidden',
            borderRadius: 0,
            border: selected ? '1px solid #2372B9' : '1px solid #D8D8D8',
            boxShadow: selected ? '0 3px 12px rgba(0, 0, 0, 0.24)' : '0 1px 4px rgba(0, 0, 0, 0.12)',
            '&:hover': { border: '1px solid #2372B9' },
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 34,
            px: 1.5,
            color: '#fff',
            background: 'linear-gradient(90deg, #2372B9 0%, #0085A8 100%)',
          }}>
            <WorkflowIcon width={22} height={22} color="#fff" aria-label={`${title} icon`} />
            <Typography sx={{ color: '#fff', fontSize: 18, lineHeight: 1.2, fontWeight: 400, paddingY: 1 }}>
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              alignItems: 'stretch',
              px: 2.25,
              pt: 2.25,
              pb: selected ? 0 : 2.25,
              minWidth: 0,
            }}
          >
            {badge && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 40,
                px: 1.25,
                border: `1px solid ${isRestricted ? '#F28C28' : isIdeation ? '#8A8D8F' : isGuidance ? theme.palette.prime.green : '#168DDD'}`,
                color: isRestricted ? '#9A5A17' : isIdeation ? '#5A5D61' : isGuidance ? theme.palette.prime.green : '#195273',
                fontSize: 13,
                fontWeight: typographyTokens.fontWeights.semibold,
              }}>
                <i className={`fa-solid ${isRestricted ? 'fa-triangle-exclamation' : isIdeation ? 'fa-triangle-exclamation' : isGuidance ? 'fa-triangle-exclamation' : 'fa-circle-info'}`} style={{ marginRight: 10 }} />
                {badge}
              </Box>
            )}
            {!badge && <Box sx={{ minHeight: 40 }} />}
            {(multiselectTooltipValue || description) && (
              <Box 
                sx={{ 
                  mt: 0,
                  flex: 1,
                  color: '#222',
                  fontSize: 13,
                  lineHeight: 1.35,
                  ...nestedListStyles,
                }}
                dangerouslySetInnerHTML={{ 
                  __html: transformQuillListToNested(multiselectTooltipValue || description || '') 
                }}
              />
            )}
            {selected && onContinue && (
              <Box sx={{ mt: 1.5, mx: -2.25, px: 2.25, pb: 1.25 }}>
                <Button
                  fullWidth
                  size="medium"
                  variant="contained"
                  onClick={(event) => {
                    event.stopPropagation();
                    onContinue();
                  }}
                  sx={{ borderRadius: '2px', minHeight: 29, fontSize: 12 }}
                >
                  Continue <i className="fa-solid fa-chevron-right" style={{ marginLeft: 8, fontSize: 10 }} />
                </Button>
              </Box>
            )}
          </Box>
        </Item>
      </Grid>
    );
  }

  // Default variant - original behavior with icons and tooltips
  return (
    <>
    <Tooltip placement="bottom" title={multiselectTooltipValue?.trim()}>
      <Grid item xs={6} sm={4} md={3} lg={saveOptions()} xl={saveOptions()}>
        <Item val={value} onClick={onClick} selected={selected}>
          {/* Icon Box */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              fontSize: typographyTokens.fontSizes.xl,
              color: '#7E8083',
            }}
          >
            {description && (
              <Tooltip placement="top" title={description}>
                {<i className={`fa-solid fa-circle-info`}></i>}
              </Tooltip>
            )}
          </Box>
          <Box
            py={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {/* Icon Box */}
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: typographyTokens.fontSizes.h2,
                color: !selected ? '#2372B9' : '#6BA543',
              }}
            >
              {!selected ? (
                <i className={`fa-solid fa-${icon || 'atom'}`}></i>
              ) : (
                <i className="fa-solid fa-circle-check"></i>
              )}
            </Box>
            {/* Caption Box */}
            <Box>
              <p
                style={{
                  font: ' 14px/22px Open Sans',
                  fontWeight: typographyTokens.fontWeights.semibold,
                  color: theme.palette.gray.solidGray,
                }}
              >
                {title}
              </p>
            </Box>
          </Box>
        </Item>
      </Grid>
      </Tooltip>
    </>
  );
};

export default CustomButton;
